import { ContentSuspense } from "components/ui/content-suspense";
import { Link } from "components/ui/link";
import { ChevronRight } from "lucide-react";
import { useEffect, useTransition } from "react";
import {
  graphql,
  PreloadedQuery,
  useFragment,
  usePreloadedQuery,
  useQueryLoader,
  useRefetchableFragment,
} from "react-relay";
import { useNavigate,useParams } from "react-router";
import { usePusherEvent } from "src/hooks/use-pusher-event";

import { DagvestigationDetails_alert$key } from "./__generated__/DagvestigationDetails_alert.graphql";
import { DagvestigationDetails_jobRun$key } from "./__generated__/DagvestigationDetails_jobRun.graphql";
import { DagvestigationDetailsQuery } from "./__generated__/DagvestigationDetailsQuery.graphql";
import { DagvestigationJobDetails } from "./components/DagvestigationJobDetails";

// Main query that loads the job run with its alert
const DagvestigationDetailsQueryDocument = graphql`
  query DagvestigationDetailsQuery($id: GlobalID!) {
    me {
      ...appMe
    }
    node(id: $id) {
      ... on GraphQLJobRun {
        ...DagvestigationDetails_jobRun
        job {
          id
          alert {
            ...DagvestigationDetails_alert
          }
        }
      }
    }
  }
`;

// Fragment for alert breadcrumb information
const dagvestigationDetailsAlertFragment = graphql`
  fragment DagvestigationDetails_alert on GraphQLAlert {
    id
    name
  }
`;

// Refetchable fragment for the job run
const dagvestigationDetailsJobRunFragment = graphql`
  fragment DagvestigationDetails_jobRun on GraphQLJobRun
  @refetchable(queryName: "DagvestigationDetailsJobRunRefetchQuery") {
    id
    name
    ...DagvestigationJobDetails_jobRun
    investigation {
      id
      summary
    }
  }
`;

// Breadcrumb component that uses the alert fragment
function DagvestigationBreadcrumb({
  alert,
  jobRunName,
}: {
  alert: DagvestigationDetails_alert$key | null;
  jobRunName: string;
}) {
  const alertData = alert ? useFragment(dagvestigationDetailsAlertFragment, alert) : null;

  if (alertData) {
    return (
      <div className="flex flex-wrap items-center">
        <Link
          to="/alerts"
          className="text-primary hover:text-primary/80 transition-colors no-underline hover:no-underline"
        >
          Alert Agents
        </Link>
        <ChevronRight className="mx-2 w-3 h-3 shrink-0" />
        <Link
          to={`/alerts/${alertData.id}`}
          className="text-primary hover:text-primary/80 transition-colors no-underline hover:no-underline"
        >
          {alertData.name}
        </Link>
        <ChevronRight className="mx-2 w-3 h-3 shrink-0" />
        <span className="text-muted-foreground">{jobRunName}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center">
      <Link
        to="/alerts"
        className="text-primary hover:text-primary/80 transition-colors no-underline hover:no-underline"
      >
        Alert Agents
      </Link>
      <ChevronRight className="mx-2 w-3 h-3 shrink-0" />
      <span className="text-muted-foreground">{jobRunName}</span>
    </div>
  );
}

// Component for valid job runs with hooks
function ValidJobRunContent({
  jobRun,
  alert,
}: {
  jobRun: DagvestigationDetails_jobRun$key;
  alert: DagvestigationDetails_alert$key | null;
}) {
  const [, startTransition] = useTransition();
  
  const [jobRunData, refetch] = useRefetchableFragment(
    dagvestigationDetailsJobRunFragment,
    jobRun
  );

  // Set up Pusher subscription for JOB_RUN_UPDATED events
  usePusherEvent<{ id: string }>({
    channelId: jobRunData.id,
    eventType: "JOB_RUN_UPDATED",
    onEvent: (eventData) => {
      if (eventData.id !== jobRunData.id) return;

      startTransition(() => {
        refetch({}, { fetchPolicy: "network-only" });
      });
    },
  });

  return (
    <div className="h-full w-full overflow-y-auto overflow-x-hidden custom-scrollbar">
      <div className="p-4 sm:p-6">
        {/* Breadcrumb */}
        <div className="space-y-3">
          <div className="text-sm text-slate-400">
            <DagvestigationBreadcrumb alert={alert} jobRunName={jobRunData.name} />
          </div>

          <div>
            <DagvestigationJobDetails jobRun={jobRunData} />
          </div>
        </div>
      </div>
    </div>
  );
}

// Component that renders the content with refetchable fragment and breadcrumb
function DagvestigationContent({
  queryRef,
}: {
  queryRef: PreloadedQuery<DagvestigationDetailsQuery>;
}) {
  const data = usePreloadedQuery<DagvestigationDetailsQuery>(
    DagvestigationDetailsQueryDocument,
    queryRef
  );
  const navigate = useNavigate();

  const jobRun = data.node;
  
  // Handle redirect for non-existent job runs
  useEffect(() => {
    if (!jobRun) {
      navigate("/alerts");
    }
  }, [jobRun, navigate]);

  // If no job run, return null while redirect happens
  if (!jobRun) {
    return null;
  }
  
  // Get the alert directly from the job
  const alert = jobRun.job?.alert || null;

  return (
    <div className="h-full w-full">
      <ValidJobRunContent 
        jobRun={jobRun as DagvestigationDetails_jobRun$key}
        alert={alert as DagvestigationDetails_alert$key | null}
      />
    </div>
  );
}

export function DagvestigationDetails() {
  const { jobRunId } = useParams<{ jobRunId: string }>();
  
  const [queryRef, loadQuery] = useQueryLoader<DagvestigationDetailsQuery>(
    DagvestigationDetailsQueryDocument
  );

  useEffect(() => {
    if (jobRunId) {
      loadQuery({ id: jobRunId });
    }
  }, [jobRunId, loadQuery]);

  if (!jobRunId) {
    return <div>Invalid job run ID</div>;
  }

  return (
    <div className="h-full w-full">
      {queryRef ? (
        <ContentSuspense>
          <DagvestigationContent queryRef={queryRef} />
        </ContentSuspense>
      ) : (
        <div />
      )}
    </div>
  );
}

export default DagvestigationDetails;